import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from '../models/api-response.model';

type ErrorBody = ApiResponse<unknown> & {
  Message?: string;
  title?: string;
  detail?: string;
  Detail?: string;
  errors?: Record<string, string[] | string>;
};

/**
 * Extracts a user-facing message from an HTTP/API error.
 * Priority: API message → validation errors → ProblemDetails detail → fallback.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!error) {
    return fallback;
  }

  if (error instanceof HttpErrorResponse) {
    const fromBody = extractMessageFromBody(error.error);
    if (fromBody) {
      return fromBody;
    }

    if (error.status === 0) {
      return 'Unable to connect to the server. Please check your connection.';
    }
  } else if (typeof error === 'object') {
    const fromBody = extractMessageFromBody(error);
    if (fromBody) {
      return fromBody;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    const msg = error.message.trim();
    if (!msg.startsWith('Http failure response')) {
      return msg;
    }
  }

  return fallback;
}

function extractMessageFromBody(body: unknown): string | null {
  if (body == null) {
    return null;
  }

  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed) {
      return null;
    }
    try {
      return extractMessageFromBody(JSON.parse(trimmed));
    } catch {
      return trimmed;
    }
  }

  if (typeof body !== 'object') {
    return null;
  }

  const payload = body as ErrorBody;

  const apiMessage = payload.message ?? payload.Message;
  if (typeof apiMessage === 'string' && apiMessage.trim()) {
    return apiMessage.trim();
  }

  const validationMessage = firstValidationError(payload.errors);
  if (validationMessage) {
    return validationMessage;
  }

  const detail = payload.detail ?? payload.Detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }

  const title = payload.title;
  if (typeof title === 'string' && title.trim()) {
    return title.trim();
  }

  return null;
}

function firstValidationError(
  errors: Record<string, string[] | string> | undefined,
): string | null {
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  for (const value of Object.values(errors)) {
    if (Array.isArray(value)) {
      const first = value.find((v) => typeof v === 'string' && v.trim());
      if (first) {
        return first.trim();
      }
    } else if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}
